import { useState, useEffect } from 'react';
import { dataLayer, type PersonalRecords } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { hapticManager } from '../../utils/hapticManager';

/**
 * useLabData — leest de per-leerling labgegevens (laatste sessie, net
 * ontgrendelde uitdaging, persoonlijke records).
 *
 * De drie sleutels hieronder waren niet gescoped op leerling. Op een gedeeld
 * apparaat las kind B daardoor de records van kind A, kreeg het "Verder waar je
 * was" met A's laatste sessie, en zag het A's ontgrendelde uitdaging gevierd
 * worden. De scoping zit nu in de dataLayer; deze hook levert de id.
 *
 * `viewingLearnerId` gaat vóór het actieve account: kijkt een ouder of leerkracht
 * mee, dan horen de records van dát kind te verschijnen en niet die van de
 * volwassene die meekijkt (die heeft er geen).
 */
export function useLabData() {
  const {
    activeAccount,
    viewingLearnerId
  } = useAppStore();
  const learnerId = viewingLearnerId || activeAccount?.id || '';
  const [lastSession, setLastSession] = useState<{
    modeId: string;
    familyId?: string;
    timestamp: number;
    score?: number;
  } | null>(null);
  const [newlyUnlockedFamilyId, setNewlyUnlockedFamilyId] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [personalRecords, setPersonalRecords] = useState<PersonalRecords>({});
  useEffect(() => {
    // Zonder id niets lezen: dan zou de ongescope fallback-sleutel worden
    // gelezen, en dat is precies het gedrag dat we hier weghalen.
    if (!learnerId) return;
    let cancelled = false;
    dataLayer.getLastOefenlabSession(learnerId).then(session => {
      if (!cancelled && session) setLastSession(session);
    }).catch(() => {});
    dataLayer.getNewlyUnlockedChallenge(learnerId).then(familyId => {
      if (cancelled || !familyId) return;
      setNewlyUnlockedFamilyId(familyId);
      setShowCelebration(true);
      playSoundIntent('reward.achievement');
      hapticManager.hapticHeavy();
      dataLayer.clearNewlyUnlockedChallenge(learnerId);
    }).catch(() => {});
    dataLayer.getPersonalRecords(learnerId).then(records => {
      if (!cancelled) setPersonalRecords(records);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
    // Wisselt de leerling (accountwissel, of een ouder die een ander kind
    // opent), dan moeten deze drie opnieuw worden gelezen. Stond dit op `[]`,
    // dan bleef het beeld van het vorige kind staan.
  }, [learnerId]);
  return {
    lastSession,
    newlyUnlockedFamilyId,
    setNewlyUnlockedFamilyId,
    showCelebration,
    personalRecords
  };
}